/**
 * DEV-959. Память навигации по спискам (клиенты/сделки/агенты): запоминаем последний
 * URL списка с фильтрами и позицию прокрутки, чтобы точки возврата (хлебная крошка,
 * кнопки «Назад») вели обратно с сохранённым состоянием.
 *
 * Живёт только в памяти (без persist) — при полной перезагрузке страницы список сам
 * получит фильтры из своего URL; «запомненный URL» нужен точкам возврата внутри одной
 * SPA-сессии. Лежит на верхнем уровне stores/ (как userDirectory), т.к. используется
 * тремя разными фичами сразу: clients, deals, agents.
 */
import { ref } from 'vue'
import { defineStore } from 'pinia'

export type ListSection =
  | 'clients'
  | 'deals'
  | 'agents'
  | 'properties'
  | 'videomeetings'
  | 'chats'
  | 'calls'

interface ListNavState {
  fullPath: string // route.fullPath списка на момент последнего изменения фильтров/страницы
  scrollTop: number
  /**
   * DEV-2771. Сколько строк было загружено в списке с «Показать ещё» (чаты, звонки): при
   * возврате список дотягивает столько же, иначе прокрутку некуда восстанавливать.
   * Относится к fullPath — другой срез начинается с первой страницы.
   */
  loadedCount: number
}

export const useListNavigationStore = defineStore('listNavigation', () => {
  const state = ref<Partial<Record<ListSection, ListNavState>>>({})

  const rememberUrl = (section: ListSection, fullPath: string) => {
    const prev = state.value[section]
    state.value[section] = {
      scrollTop: prev?.scrollTop ?? 0,
      fullPath,
      loadedCount: prev?.fullPath === fullPath ? prev.loadedCount : 0,
    }
  }

  const rememberScroll = (section: ListSection, scrollTop: number) => {
    const entry = state.value[section]
    if (entry) entry.scrollTop = scrollTop
  }

  const rememberLoadedCount = (section: ListSection, count: number) => {
    const entry = state.value[section]
    if (entry) entry.loadedCount = count
  }

  const urlFor = (section: ListSection): string | undefined => state.value[section]?.fullPath

  const scrollFor = (section: ListSection): number => state.value[section]?.scrollTop ?? 0

  const loadedCountFor = (section: ListSection): number => state.value[section]?.loadedCount ?? 0

  return {
    rememberUrl,
    rememberScroll,
    rememberLoadedCount,
    urlFor,
    scrollFor,
    loadedCountFor,
  }
})
